import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import {
  POINTS_LIST,
  POINTS_LOG_LIST,
  USER_LIST_DROPDOWN,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CITY_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
} from "../../routes/routes";
import { PAGINATE, NOPAGINATE } from "../../constant/constant";
import { NoDataFound, Pagination, hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import {
  styleExcelHeaderRow,
  styleExcelTitleRow,
} from "../../utils/excelStyles";
import Select from "react-select";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import Loader from "../../components/common/Loader";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import StickyTable from "../../components/common/StickyTable";

const MonthlyPointsReportsList = () => {
  const { hasPermission } = usePermission();

  const monthOptions = React.useMemo(() => {
    const list = [];
    const now = moment();
    for (let i = 0; i < 24; i++) {
      const m = now.clone().subtract(i, "months");
      list.push({
        value: m.format("YYYY-MM"),
        label: m.format("MMMM YYYY"),
        from_date: m.clone().startOf("month").format("DD/MM/YYYY"),
        to_date: m.clone().endOf("month").format("DD/MM/YYYY"),
      });
    }
    return list;
  }, []);

  const defaultMonth = monthOptions[0];

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [detailData, setDetailData] = useState([]);
  const [detailsModal, setDetailModal] = useState(false);
  const [btnExcelDisabled, setBtnExcelDisabled] = useState(false);
  const [btnPdfDisabled, setBtnPdfDisabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState({
    from_date: defaultMonth.from_date,
    to_date: defaultMonth.to_date,
  });

  const [userOptions, setUserOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [showClearBtn, setShowClearBtn] = useState(true);

  const refFlatpickr = useRef(null);
  const selectStateRef = useRef(null);
  const selectCityRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);
  const selectMemberRef = useRef(null);
  const selectMonthRef = useRef(null);

  const handleStateClear = () => {
    if (selectStateRef.current) selectStateRef.current.clearValue();
    if (selectCityRef.current) selectCityRef.current.clearValue();
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const handleCityClear = () => {
    if (selectCityRef.current) selectCityRef.current.clearValue();
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
  };

  const handleRegionClear = () => {
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const handleChapterClear = () => {
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const handleMemberClear = () => {
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const handleMonthChange = (selected) => {
    setSelectedMonth(selected);
    if (selected && selected.from_date && selected.to_date) {
      setFilter((prev) => ({
        ...prev,
        from_date: selected.from_date,
        to_date: selected.to_date,
      }));
      if (refFlatpickr.current?.flatpickr) {
        refFlatpickr.current.flatpickr.setDate([selected.from_date, selected.to_date]);
        setShowClearBtn(true);
      }
    }
  };

  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    if (refFlatpickr.current?.flatpickr) {
      refFlatpickr.current.flatpickr.setDate([]);
    }
    setFilter((prev) => ({
      ...prev,
      from_date: "",
      to_date: "",
    }));
  };

  // Columns definition
  const columns = [{ header: "Sl.No.", key: "sno" }];
  columns.push({ header: "Member", key: "name" });
  columns.push({ header: "Attendance", key: "attendance_points" });
  columns.push({ header: "Referral", key: "referral_points" });
  columns.push({ header: "Visitor", key: "visitor_points" });
  columns.push({ header: "Business", key: "business_points" });
  columns.push({ header: "Testimonial", key: "testimonial_points" });
  columns.push({ header: "Training", key: "training_points" });
  columns.push({ header: "Total Points", key: "points_earned" });
  if (hasRole([1])) {
    columns.push({ header: "Country", key: "country_name" });
  }
  if (hasRole([1, 6])) {
    columns.push({ header: "State", key: "state_name" });
  }
  if (hasRole([1, 6, 7])) {
    columns.push({ header: "City", key: "city_name" });
  }
  if (hasRole([1, 6, 7, 8])) {
    columns.push({ header: "Region", key: "region_name" });
  }
  if (hasRole([1, 6, 7, 8, 2])) {
    columns.push({ header: "Circle", key: "chapter_name" });
  }

  const monthLabel = selectedMonth ? selectedMonth.label : "Monthly";
  const { from_date, to_date } = filter;
  let workSheetName = `Monthly Points (${monthLabel})`;
  if (from_date && to_date) {
    workSheetName += ` [${from_date} - ${to_date}]`;
  }
  const title = workSheetName;
  const fileName = `Monthly_Points_Report_${monthLabel.replace(/\s+/g, "_")}`;

  const workbook = new Excel.Workbook();

  const exportAsExcel = async () => {
    try {
      const worksheet = workbook.addWorksheet(fileName);
      worksheet.columns = columns;
      worksheet.insertRow(1, []);

      const totalCols = columns.length || 1;
      const lastColLetter = String.fromCharCode(64 + totalCols);
      worksheet.mergeCells(`A1:${lastColLetter}1`);
      const titleCell = worksheet.getCell("A1");
      titleCell.value = title;
      styleExcelTitleRow(worksheet.getRow(1), totalCols);

      const headerRow = worksheet.getRow(2);
      styleExcelHeaderRow(headerRow, totalCols);

      worksheet.columns.forEach((column) => {
        column.width = (column.header ? column.header.length : 10) + 15;
        column.alignment = { horizontal: "center" };
      });

      setBtnExcelDisabled(true);
      const response = await axios.get(POINTS_LIST, {
        params: {
          type: NOPAGINATE,
          isAdmin: 1,
          ...filter,
        },
      });

      if (response.status === 200 && response.data.success) {
        const excelData = response.data.data.points;
        if (!excelData || excelData.length < 1) {
          toast.error("No data found!");
          return;
        }

        excelData.forEach((singleData) => {
          worksheet.addRow(singleData);
        });

        worksheet.eachRow({ includeEmpty: false }, (row) => {
          const currentCell = row._cells;
          currentCell.forEach((singleCell) => {
            const cellAddress = singleCell._address;
            worksheet.getCell(cellAddress).border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `${fileName}.xlsx`);
      }
    } catch (error) {
      console.error("Excel Export Error", error);
      toast.error(error.message || "Failed to export Excel");
    } finally {
      setBtnExcelDisabled(false);
      try {
        workbook.removeWorksheet(fileName);
      } catch (e) {}
    }
  };

  const exportAsPDF = async () => {
    try {
      const doc = new jsPDF("landscape");
      const headers = ["Sl.No.", "Member", "Attendance", "Referral", "Visitor", "Business", "Testimonial", "Training", "Total"];
      if (hasRole([1])) headers.push("Country");
      if (hasRole([1, 6])) headers.push("State");
      if (hasRole([1, 6, 7])) headers.push("City");
      if (hasRole([1, 6, 7, 8])) headers.push("Region");
      if (hasRole([1, 6, 7, 8, 2])) headers.push("Circle");

      setBtnPdfDisabled(true);
      const response = await axios.get(POINTS_LIST, {
        params: {
          type: NOPAGINATE,
          isAdmin: 1,
          ...filter,
        },
      });

      if (response.status === 200 && response.data.success) {
        const points = response.data.data.points || [];
        if (points.length < 1) {
          toast.error("No data found!");
          return;
        }

        const pdfData = points.map((p) => {
          const row = [
            p.sno,
            p.name,
            p.attendance_points,
            p.referral_points,
            p.visitor_points,
            p.business_points,
            p.testimonial_points,
            p.training_points,
            p.points_earned,
          ];
          if (hasRole([1])) row.push(p.country_name);
          if (hasRole([1, 6])) row.push(p.state_name);
          if (hasRole([1, 6, 7])) row.push(p.city_name);
          if (hasRole([1, 6, 7, 8])) row.push(p.region_name);
          if (hasRole([1, 6, 7, 8, 2])) row.push(p.chapter_name);
          return row;
        });

        const pdfTitle = title;
        const textWidth =
          (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
          doc.internal.scaleFactor;
        const textOffset = (doc.internal.pageSize.width - textWidth) / 2;
        doc.text(pdfTitle, textOffset, 12);

        doc.autoTable({
          head: [headers],
          body: pdfData,
          margin: { top: 20, right: 15, bottom: 15, left: 15 },
          startY: 20,
          headerStyles: {
            fillColor: [207, 32, 48],
            textColor: [255, 255, 255],
          },
          styles: { lineWidth: 0.1 },
        });

        doc.save(`${fileName}.pdf`);
      }
    } catch (error) {
      console.error("PDF Export Error", error);
      toast.error(error.message || "Failed to export PDF");
    } finally {
      setBtnPdfDisabled(false);
    }
  };

  const fetchPointsLogData = async (id) => {
    try {
      const res = await axios.get(POINTS_LOG_LIST, {
        params: {
          isAdmin: 1,
          points_user_id: id,
          ...filter,
        },
      });
      if (res.status === 200 && res.data.success) {
        setDetailData(res.data.data.points_log || []);
      }
    } catch (error) {
      console.error("Error fetching breakdown:", error);
    }
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(POINTS_LIST, {
        params: {
          type: PAGINATE,
          page: page,
          perPage: perPage,
          isAdmin: 1,
          ...filter,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          setData(response.data.data.points || []);
          setPaginator({
            prev: response.data.data.prev_page_url != null,
            next: response.data.data.next_page_url != null,
            total: response.data.data.total,
            current: response.data.data.current_page,
            last: response.data.data.last_page,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });

    const fetchData = async () => {
      try {
        const userResponse = await axios.get(USER_LIST_DROPDOWN, {
          params: { ...filter, exclude_other_region_chapter_users: 1 },
        });
        setUserOptions(userResponse.data.data || []);

        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        setCountryOptions(countryResponse.data.data || []);

        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, { params: { ...filter } });
        setStateOptions(stateResponse.data.data || []);

        const cityResponse = await axios.get(CITY_LIST_DROPDOWN, { params: { ...filter } });
        setCityOptions(cityResponse.data.data || []);

        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, { params: { ...filter } });
        setRegionOptions(regionResponse.data.data || []);

        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, { params: { ...filter } });
        setChapterOptions(chapterResponse.data.data || []);
      } catch (error) {
        console.error("Error fetching dropdowns:", error);
      }
    };
    fetchData();
  }, [page, filter, perPage]);

  const handleCalendarClick = () => {
    if (refFlatpickr.current) {
      refFlatpickr.current.flatpickr.open();
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-3">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Monthly Points Report</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Monthly Points Report</span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                {hasPermission("reports-reward-points-export") && (
                  <>
                    <button
                      disabled={btnExcelDisabled}
                      type="button"
                      onClick={exportAsExcel}
                      className="btn rounded-pill btn-primary excel-btn ibutton"
                    >
                      <span className="tf-icons bx bxs-spreadsheet"></span>
                      &nbsp; Excel
                    </button>
                    <button
                      disabled={btnPdfDisabled}
                      type="button"
                      onClick={exportAsPDF}
                      className="btn rounded-pill btn-primary pdf-btn ibutton"
                    >
                      <span className="tf-icons bx bxs-file-pdf"></span>&nbsp;
                      Download PDF
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1">
        <div className="card icard sticky-table-card">
          <div className="container-fluid f_containter">
            <div className="row mb-3 gx-3">
              {hasRole([1]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="country_id">
                    Country
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    name="country_id"
                    isClearable
                    options={countryOptions}
                    onChange={(selected) => {
                      handleStateClear();
                      setFilter({
                        ...filter,
                        country_id: selected?.value,
                        state_id: null,
                        city_id: null,
                        region_id: null,
                        chapter_id: null,
                        user_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="state_id">
                    State
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    ref={selectStateRef}
                    name="state_id"
                    isClearable
                    options={stateOptions}
                    onChange={(selected) => {
                      handleCityClear();
                      setFilter({
                        ...filter,
                        state_id: selected?.value,
                        city_id: null,
                        region_id: null,
                        chapter_id: null,
                        user_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7]) && (
                <div className="col-md-3 mb-3">
                  <label className="form-label" htmlFor="city_id">
                    City
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    ref={selectCityRef}
                    name="city_id"
                    isClearable
                    options={cityOptions}
                    onChange={(selected) => {
                      handleRegionClear();
                      setFilter({
                        ...filter,
                        city_id: selected?.value,
                        region_id: null,
                        chapter_id: null,
                        user_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7, 8]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="region_id">
                      Region
                    </label>
                    <Select
                      ref={selectRegionRef}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      name="region_id"
                      isClearable
                      options={regionOptions}
                      onChange={(selected) => {
                        handleChapterClear();
                        setFilter({
                          ...filter,
                          region_id: selected?.value,
                          chapter_id: null,
                          user_id: null,
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {hasRole([1, 6, 7, 8, 2]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="chapter_id">
                      Circle
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      ref={selectChapterRef}
                      name="chapter_id"
                      isClearable
                      options={chapterOptions}
                      onChange={(selected) => {
                        handleMemberClear();
                        setFilter({
                          ...filter,
                          chapter_id: selected?.value || null,
                          user_id: null,
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="user_id">
                    Member
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    ref={selectMemberRef}
                    name="user_id"
                    isClearable
                    options={userOptions}
                    onChange={(selected) =>
                      setFilter({ ...filter, user_id: selected?.value })
                    }
                    placeholder="Member"
                  />
                </div>
              </div>

              {/* Dedicated Month Selector */}
              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="month_select">
                    Calendar Month <span className="text-danger">*</span>
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    ref={selectMonthRef}
                    name="month_select"
                    value={selectedMonth}
                    options={monthOptions}
                    onChange={handleMonthChange}
                    placeholder="Select Month"
                  />
                </div>
              </div>

              {/* Exact Date Range for the Month */}
              <div className="col-md-3">
                <div className="mb-3 dateRangeDiv">
                  <label className="form-label" htmlFor="date_range">
                    Date Range (Month Window)
                  </label>
                  <div className="datepicker_wrapper">
                    <div className="datepicker">
                      <Flatpickr
                        ref={refFlatpickr}
                        className="form-control"
                        options={{
                          mode: "range",
                          dateFormat: "d/m/Y",
                          defaultDate: [defaultMonth.from_date, defaultMonth.to_date],
                          onChange: (selected) => {
                            setShowClearBtn(true);
                            if (selected[0] && selected[1]) {
                              setFilter({
                                ...filter,
                                from_date: moment(selected[0]).format("DD/MM/YYYY"),
                                to_date: moment(selected[1]).format("DD/MM/YYYY"),
                              });
                            }
                          },
                        }}
                        placeholder="Date Range"
                      />
                      {showClearBtn && (
                        <button
                          className="border-0 clearDateRangeBtn"
                          title="Reset"
                          onClick={() => {
                            handleMonthChange(defaultMonth);
                          }}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      )}
                    </div>
                    <div className="cal_icon" onClick={handleCalendarClick}>
                      <span className="input-group-text calendar-icon"></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info banner */}
              {/* <div className="col-12 mt-1 mb-2">
                <div className="alert alert-success d-flex align-items-center mb-0 py-2 px-3" role="alert">
                  <i className="bx bx-calendar-check fs-4 me-2"></i>
                  <div>
                    <strong>Monthly Points Report:</strong> Displaying points calculated strictly from activities (Attendance, Referrals, Visitors, Business, Testimonials, Training) between <strong>{filter.from_date || "N/A"}</strong> and <strong>{filter.to_date || "N/A"}</strong> ({monthLabel}).
                  </div>
                </div>
              </div> */}
            </div>
          </div>

          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}

          <StickyTable refreshDeps={[data, loading]}>
            <table className="table table-striped itable">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Member</th>
                  <th>Attendance</th>
                  <th>Referral</th>
                  <th>Visitor</th>
                  <th>Business</th>
                  <th>Testimonial</th>
                  <th>Training</th>
                  <th>Total</th>
                  {hasRole([1]) && <th>Country</th>}
                  {hasRole([1, 6]) && <th>State</th>}
                  {hasRole([1, 6, 7]) && <th>City</th>}
                  {hasRole([1, 6, 7, 8]) && <th>Region</th>}
                  {hasRole([1, 6, 7, 8, 2]) && <th>Circle</th>}
                  <th style={{ textAlign: "center", width: "10%" }}>Action</th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {loading ? (
                  <Loader loading={loading} colspan="15" />
                ) : data.length > 0 ? (
                  data.map((points, i) => (
                    <tr key={i}>
                      <td>{points?.sno}</td>
                      <td>
                        <span className="table_img pr-2">
                          <img src={points?.profile_image} alt="icon" />
                        </span>
                        {points?.name}
                      </td>
                      <td>{points?.attendance_points}</td>
                      <td>{points?.referral_points}</td>
                      <td>{points?.visitor_points}</td>
                      <td>{points?.business_points}</td>
                      <td>{points?.testimonial_points}</td>
                      <td>{points?.training_points}</td>
                      <td><strong>{points?.points_earned}</strong></td>
                      {hasRole([1]) && <td>{points?.country_name}</td>}
                      {hasRole([1, 6]) && <td>{points?.state_name}</td>}
                      {hasRole([1, 6, 7]) && <td>{points?.city_name}</td>}
                      {hasRole([1, 6, 7, 8]) && <td>{points?.region_name}</td>}
                      {hasRole([1, 6, 7, 8, 2]) && <td>{points?.chapter_name}</td>}
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="badge bg-label-info me-1 border-0"
                          title="View Details"
                          onClick={() => {
                            setDetailModal(true);
                            fetchPointsLogData(points.id);
                          }}
                        >
                          <i className="bx bx-show"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <NoDataFound colspan="15" message="No Data Found For This Month" />
                )}
              </tbody>
            </table>
          </StickyTable>

          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
        </div>
      </div>

      {/* Details Breakdown Modal */}
      <Modal
        show={detailsModal}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setDetailModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter" className="modal_title1">
            Monthly Points Breakdown ({monthLabel})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <StickyTable refreshDeps={[detailData, loading]}>
            <table className="table table-striped itable">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Metric</th>
                  <th>Raw Value</th>
                  <th>Points</th>
                  <th>Month Window</th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {detailData.length > 0 ? (
                  detailData.map((log, i) => (
                    <tr
                      key={i}
                      style={
                        log.action_type === "total"
                          ? { fontWeight: "bold", background: "#f8f9fa" }
                          : {}
                      }
                    >
                      <td>{log?.sno}</td>
                      <td>{log?.action_type_text}</td>
                      <td>
                        {log.action_type === "total"
                          ? "-"
                          : log.value_type === "amount"
                          ? `₹${Number(log.raw_value || 0).toLocaleString("en-IN")}`
                          : log.raw_value}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            log.points_earned > 0 ? "bg-label-success" : "bg-label-secondary"
                          }`}
                        >
                          {log?.points_earned}
                        </span>
                      </td>
                      <td>
                        {log.action_type === "total"
                          ? "-"
                          : `${log.from_date} to ${log.to_date}`}
                      </td>
                    </tr>
                  ))
                ) : (
                  <NoDataFound colspan="5" message="No breakdown available" />
                )}
              </tbody>
            </table>
          </StickyTable>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MonthlyPointsReportsList;
